// Web Push subscription helpers for the patient PWA.
import api from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) throw new Error("Service Worker no soportado");
  const reg = await navigator.serviceWorker.getRegistration();
  return reg || navigator.serviceWorker.register("/sw.js");
}

export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function currentPushPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Notifiche non supportate su questo dispositivo");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permesso negato");
  await ensureServiceWorker();
  const reg = await navigator.serviceWorker.ready;

  // Fetch current VAPID key
  const { data } = await api.get("/push/vapid-public-key");
  if (!data?.publicKey) throw new Error("VAPID key non disponibile");
  const appServerKey = urlBase64ToUint8Array(data.publicKey);

  // If there is an existing subscription, make sure it was made with the SAME
  // VAPID key. If the server rotated keys the browser will otherwise refuse
  // to re-subscribe and throw "Registration failed – A subscription with a
  // different applicationServerKey already exists."
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try {
      const oldKey = new Uint8Array(existing.options?.applicationServerKey || []);
      const same = oldKey.length === appServerKey.length &&
        oldKey.every((b, i) => b === appServerKey[i]);
      if (same) {
        // Re-register on the server (idempotent) then return.
        await api.post("/push/patient/subscribe", {
          subscription: existing.toJSON(),
          userAgent: navigator.userAgent,
        });
        return existing;
      }
      await existing.unsubscribe();
    } catch { /* fall through and try a fresh subscribe */ }
  }

  let sub;
  try {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    });
  } catch (err) {
    const name = err?.name || "";
    const raw = err?.message || String(err);
    // Chrome throws a generic "Registration failed" DOMException in several cases.
    // Map the most common ones to actionable Italian text.
    if (name === "NotAllowedError") throw new Error("Le notifiche sono state bloccate dal browser");
    if (name === "AbortError" || /registration failed/i.test(raw)) {
      throw new Error("Il servizio di notifiche non è raggiungibile su questo dispositivo. Prova a riavviare il browser, disattivare eventuali VPN o riprovare da Chrome/Safari aggiornato.");
    }
    if (name === "InvalidStateError") throw new Error("Un altro browser risulta già iscritto. Chiudi il browser e riprova.");
    throw new Error(`Errore notifiche: ${raw}`);
  }

  await api.post("/push/patient/subscribe", {
    subscription: sub.toJSON(),
    userAgent: navigator.userAgent,
  });
  return sub;
}

export async function unsubscribeFromPush() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await api.post("/push/patient/unsubscribe", { endpoint });
}
